/** @jsxImportSource @emotion/react */

import { useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BaseButton from "components/common/Button/Base";
import { ExtendedOption } from "components/common/Select/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import BusinessAutocomplete from "../BusinessAutocomplete";
import {
  PaymentNotice,
  PaymentNoticeMatchResult,
  PAYMENT_NOTICE_MATCH_STATUS_LABELS,
} from "models/paymentNotice.model";
import { deletePaymentNotice, matchPaymentNotice } from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import { convertCurrency, convertRegNumber } from "utils/converter";

interface MatchCandidateBusiness {
  id: string;
  name: string;
  regNumber: string;
}

interface MatchingDialogProps {
  isOpen: boolean;
  organizationId: string | null;
  unmatchedList: PaymentNotice[];
  // 매칭 후보 사업자 목록 - 급여관리 탭의 사업자별 현황 테이블에 이미 로드돼있는
  // 조직 소속 전체 사업자 목록을 그대로 재사용한다 (별도 API 호출 없음).
  businessList: MatchCandidateBusiness[];
  onClose: () => void;
  onSuccess: (selections: Record<string, string>) => void;
  // 일부만 실패했을 때, 성공한 건만 먼저 반영한다 (다이얼로그는 닫지 않고 실패한
  // 건만 남겨 재시도할 수 있게 함 - 성공한 건을 다시 전송하지 않기 위함).
  onPartialSuccess: (selections: Record<string, string>) => void;
  onDeleted: (paymentNoticeId: string) => void;
  // 업로드 직후 이어지는 매칭 흐름에서는 반드시 확인을 눌러야 다음(결과) 화면으로
  // 넘어가도록 닫기 수단을 없앤다. 독립적으로 "매칭이 필요한 납부서" 버튼으로
  // 들어온 경우에만 닫기를 허용한다.
  showCloseButton?: boolean;
}

function MatchingDialog({
  isOpen,
  organizationId,
  unmatchedList,
  businessList,
  onClose,
  onSuccess,
  onPartialSuccess,
  onDeleted,
  showCloseButton = true,
}: MatchingDialogProps) {
  const businessOptions: ExtendedOption[] = businessList.map((biz) => ({
    value: biz.id,
    label: `${biz.name} (${convertRegNumber(biz.regNumber)})`,
  }));
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const resetState = () => {
    setSelections({});
    setIsSubmitting(false);
    setDeleteTargetId(null);
    setIsDeleting(false);
  };

  const closeDialog = () => {
    if (isSubmitting) return;
    onClose();
    setTimeout(resetState, 500);
  };

  const selectBusiness = (paymentNoticeId: string) => (businessId: string) => {
    setSelections((prev) => ({ ...prev, [paymentNoticeId]: businessId }));
  };

  const confirmDelete = async () => {
    if (!deleteTargetId || isDeleting) return;

    setIsDeleting(true);
    try {
      await deletePaymentNotice(deleteTargetId);
      setSelections((prev) => {
        const next = { ...prev };
        delete next[deleteTargetId];
        return next;
      });
      onDeleted(deleteTargetId);
      setDeleteTargetId(null);
      setAlertDialogData({ message: "삭제되었습니다.", type: "success" });
      setIsAlertOpen(true);
    } catch (err: any) {
      setAlertDialogData({
        message:
          err.response?.data?.message || "납부서 삭제 중 오류가 발생했습니다.",
        type: "error",
      });
      setIsAlertOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmMatching = async () => {
    if (!organizationId || isSubmitting) return;

    // 선택한 항목만 매칭 처리하고, 선택하지 않은 항목은 매칭 실패 상태로 남겨둔다.
    const itemsToMatch = unmatchedList.filter((item) =>
      Boolean(selections[item.id])
    );

    if (itemsToMatch.length === 0) {
      onSuccess(selections);
      setTimeout(resetState, 500);
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await matchPaymentNotice(
        organizationId,
        itemsToMatch.map((item) => ({
          paymentNoticeId: item.id,
          businessId: selections[item.id],
        }))
      );
      const results: PaymentNoticeMatchResult[] = res.data || [];
      const succeededResults = results.filter((result) => result.success);
      const failedResults = results.filter((result) => !result.success);

      setIsSubmitting(false);

      if (failedResults.length > 0) {
        // 성공한 건은 재전송되지 않도록 목록에서 바로 제거하고, 실패한 건만 남겨
        // 재시도할 수 있게 한다 (API 문서 권장 방식).
        if (succeededResults.length > 0) {
          const succeededSelections: Record<string, string> = {};
          succeededResults.forEach((result) => {
            succeededSelections[result.paymentNoticeId] =
              selections[result.paymentNoticeId];
          });
          onPartialSuccess(succeededSelections);
          setSelections((prev) => {
            const next = { ...prev };
            succeededResults.forEach((result) => {
              delete next[result.paymentNoticeId];
            });
            return next;
          });
        }

        const failedItems = failedResults.map((result) => {
          const notice = itemsToMatch.find(
            (item) => item.id === result.paymentNoticeId
          );
          const fileName = notice?.originalFileName || result.paymentNoticeId;
          return result.errorMessage
            ? `${fileName} (${result.errorMessage})`
            : fileName;
        });
        setAlertDialogData({
          message: `다음 납부서의 매칭 처리에 실패했습니다: ${failedItems.join(
            ", "
          )}`,
          type: "error",
        });
        setIsAlertOpen(true);
        return;
      }
    } catch (err: any) {
      setIsSubmitting(false);
      setAlertDialogData({
        message:
          err.response?.data?.message || "매칭 처리 중 오류가 발생했습니다.",
        type: "error",
      });
      setIsAlertOpen(true);
      return;
    }

    onSuccess(selections);
    setTimeout(resetState, 500);
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={showCloseButton ? closeDialog : undefined}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          사업자 매칭이 필요한 납부서 ({unmatchedList.length}건)
        </DialogTitle>
        <DialogContent>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 12px;
            `}
          >
            {unmatchedList.map((item) => (
              <div
                key={item.id}
                css={css`
                  padding: 16px;
                  border: 1px solid ${PALETTE.border};
                  border-radius: 8px;
                  display: flex;
                  flex-direction: column;
                  gap: 10px;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 2px;
                    `}
                  >
                    <div
                      css={css`
                        font-weight: 600;
                        color: ${PALETTE.text};
                      `}
                    >
                      {item.companyName || "(인식된 상호 없음)"}
                    </div>
                    <div
                      css={css`
                        font-size: 12px;
                        color: ${PALETTE.textMid};
                      `}
                    >
                      {item.originalFileName}
                    </div>
                  </div>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 6px;
                    `}
                  >
                    <BusinessAutocomplete
                      value={selections[item.id] || ""}
                      options={businessOptions}
                      disabled={isSubmitting}
                      onSelect={selectBusiness(item.id)}
                    />
                    <IconButton
                      title="납부서 삭제"
                      size="small"
                      disabled={isSubmitting}
                      onClick={() => setDeleteTargetId(item.id)}
                    >
                      <DeleteOutlineIcon
                        fontSize="small"
                        css={css`
                          color: ${PALETTE.error};
                        `}
                      />
                    </IconButton>
                  </div>
                </div>

                <div
                  css={css`
                    display: flex;
                    flex-wrap: wrap;
                    gap: 4px 16px;
                    font-size: 13px;
                    color: ${PALETTE.textMid};
                  `}
                >
                  {item.businessNo && (
                    <span>사업자번호 {convertRegNumber(item.businessNo)}</span>
                  )}
                  {item.taxItem && <span>세목 {item.taxItem}</span>}
                  {item.period && <span>귀속기간 {item.period}</span>}
                  {typeof item.amount === "number" && (
                    <span>금액 {convertCurrency(item.amount)}원</span>
                  )}
                  <span>{PAYMENT_NOTICE_MATCH_STATUS_LABELS[item.matchStatus]}</span>
                </div>

                {item.matchNote && (
                  <div
                    css={css`
                      font-size: 12px;
                      color: ${PALETTE.error};
                    `}
                  >
                    {item.matchNote}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
        <div
          css={css`
            margin: 6px 24px;
            border-top: 1px solid ${PALETTE.border};
          `}
        />
        <DialogActions
          css={css`
            padding: 12px 24px 20px 24px;
            display: flex;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              font-size: 13px;
              color: ${PALETTE.textMid};
            `}
          >
            {Object.keys(selections).length}/{unmatchedList.length} 매칭 완료
          </span>
          <div
            css={css`
              display: flex;
              gap: 8px;
            `}
          >
            {showCloseButton && (
              <BaseButton
                variant="outlined"
                color={PALETTE.primary}
                disabled={isSubmitting}
                onClick={closeDialog}
              >
                닫기
              </BaseButton>
            )}
            <BaseButton
              variant="contained"
              color={PALETTE.primary}
              disabled={isSubmitting}
              onClick={confirmMatching}
            >
              {isSubmitting ? "처리 중" : "확인"}
            </BaseButton>
          </div>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(deleteTargetId)}
        onClose={() => !isDeleting && setDeleteTargetId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>알림</DialogTitle>
        <DialogContent>
          <DialogContentText>이 납부서를 삭제하시겠습니까?</DialogContentText>
          <DialogContentText>삭제한 데이터는 복구할 수 없습니다.</DialogContentText>
        </DialogContent>
        <DialogActions
          css={css`
            margin: 0 12px;
            padding: 12px;
            border-top: 1px solid ${PALETTE.border};
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            disabled={isDeleting}
            onClick={() => setDeleteTargetId(null)}
          >
            취소
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.error}
            disabled={isDeleting}
            onClick={confirmDelete}
          >
            {isDeleting ? "삭제 중" : "삭제"}
          </BaseButton>
        </DialogActions>
      </Dialog>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </>
  );
}

export default MatchingDialog;
